import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Payment, PrivilegeType, Salary } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { EmployeeService } from 'src/app/shared/services/employee.service';
import { DueCheckComponent } from '../payment/due-check/due-check.component';
import { PaymentCrudComponent } from '../payment/payment-crud/payment-crud.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SalaryCrudComponent } from '../salary/salary-crud/salary-crud.component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-salary-payment',
  templateUrl: './salary-payment.component.html',
  styleUrls: ['./salary-payment.component.scss']
})

export class SalaryPaymentComponent implements OnInit {
  public dueCheck: boolean;
  public isLoading: boolean = true;
  public searchKeyword: string = "";
  public salaries: Salary[] = [];
  public salary: Salary;
  public privilege: PrivilegeType;
  public frequencies: String[] = [];
  public selectedFrequency: string = 'MONTHLY';
  public fromDate: string;
  public payment: Payment;
  public totalPaid: number = 0;
  public totalDue: number = 0;

  constructor(
    private employeeService: EmployeeService,
    private commonService: CommonService,
    private modalService: NgbModal,
    private datePipe: DatePipe,
    private dataService: DataService
  ) { }

  ngOnInit() {
    this.dataService.privilege().subscribe(privilege => { this.privilege = this.commonService.getPrivileges(privilege) });
    this.employeeService.getFrequency().subscribe(frequecies => this.frequencies = frequecies);
    this.fromDate = this.datePipe.transform(this.commonService.getLastDateOfCurrentMonth(), 'yyyy-MM-dd');
    this.getActiveSalaries();
  }

  private getActiveSalaries(clear: boolean = true) {
    const fromDate = this.datePipe.transform(this.fromDate, 'yyyy-MM-dd');
    this.employeeService.getActiveSalaries(fromDate, this.selectedFrequency).subscribe({
      next: (salary: Salary[]) => {
        this.salaries = salary.sort((a, b) => {
          if (a.status === 'NIL' && b.status !== 'NIL') return -1; 
          if (a.status !== 'NIL' && b.status === 'NIL') return 1;  
          if (a.status === 'DUE' && b.status !== 'DUE') return -1;
          if (a.status !== 'DUE' && b.status === 'DUE') return 1;  
          return a.id - b.id; 
        });        
        this.isLoading = false;
        this.calculateTotals();
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  public onStatusChange() {
    this.getActiveSalaries(false);
  }

  public upsertDue(salary?: Salary) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(DueCheckComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.salaries = salary;
    modalRef.componentInstance.sid = salary.id;
    modalRef.componentInstance.date = salary.fromDate;
    modalRef.result.finally(() => {
      this.getActiveSalaries();
    });
  }

  public upsertPayment(salary: Salary) {
    if (!this.privilege.canEdit) return;
    if (salary) {
      this.employeeService.getPayment(salary.paymentId).subscribe({
        next: (payment: Payment) => {
          this.payment = payment;
          const modalRef = this.modalService.open(PaymentCrudComponent, { size: 'lg', backdrop: 'static' });
          modalRef.componentInstance.payment = this.payment;
          modalRef.componentInstance.sid = payment.salaryId;
          modalRef.componentInstance.salaryDate = payment.salaryDate;
          modalRef.result.finally(() => {
            this.getActiveSalaries();
          });
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.setToastr(500, error?.error.name || error.error);
        }
      })
    }
  }

  public upsertSalary(salary?: Salary) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(SalaryCrudComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.salary = salary;
    modalRef.componentInstance.passEntry.subscribe((response: Salary) => {
      if (response) {
        this.getActiveSalaries(true);
      }
    });
  }

  private calculateTotals() {
    this.totalPaid = this.salaries
      .filter(salary => salary.status === 'DONE' )
      .reduce((sum, salary) => sum + salary.netSalary, 0);

    this.totalDue = this.salaries
      .filter(salary => salary.status === 'DUE')
      .reduce((sum, salary) => sum + salary.netSalary, 0);
  }

  public downloadExcel() {
    const excelData = this.salaries.map((salary, index) => ({
      '#': index + 1,
      'Employee Name': salary.employeeName,
      'Date': this.datePipe.transform(salary.fromDate, 'dd/MM/yyyy'),
      'Frequency': salary.frequency,
      'PF': salary.providentFund,
      'Basic Salary': salary.basicSalary,
      'Net Salary': salary.netSalary,
      Status: salary.status,
    }));
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
    const workbook: XLSX.WorkBook = {
      Sheets: { Salaries: worksheet },
      SheetNames: ['Salaries'],
    };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(data, `Salaries_${this.datePipe.transform(new Date(), 'yyyyMMdd')}.xlsx`);
  }
}